import { DateOnly } from '@if/core/date'
import { t } from '@if/core/i18n'
import {
  EFinancingProgram,
  ELoanPurpose,
  EPaymentPlan,
  EProvince,
  getEFinancingProgramList,
  getEGenderList,
  getELanguageList,
  getELoanPurposeList,
  getEPaymentPlanList,
  getEProvinceList,
} from '@if/core/types'
import * as yup from '@if/core/yup'
import {
  EInterestComputeMethod,
  EDisbursementTarget,
  EProduct,
  getECoBorrowerRelationList,
  getEFinancingPlanList,
  getEFeeBillingMechanismList,
  getEInterestComputeMethodList,
  getEInterestRoundingMethodList,
  getEMetro2ReportingReasonList,
  getEPaymentMethodList,
  getEDisbursementTargetList,
  getEProductList,
  getETermList,
} from '@src/types/constants'

export const DisbursementLineSchema = yup.object().shape({
  productId: yup.numberEnum(getEProductList()).required().default(null),
  amount: yup
    .number()
    .required(() => t('loan.error.missingAmount'))
    .default(null),
  description: yup.string().emptyToNull().nullable().default(null),
})
export type DisbursementLineSchemaType = yup.InferType<typeof DisbursementLineSchema>

export const DisbursementSchema = yup.object().shape({
  payeeId: yup.string().required().default(null),
  payeeType: yup.numberEnum(getEDisbursementTargetList()).required().default(null),
  paymentOn: yup.dateOnly().required().default(null),
  paymentMethod: yup.numberEnum(getEPaymentMethodList()).required().default(null),
  lines: yup
    .array()
    .of(DisbursementLineSchema)
    .required()
    .unique((p: DisbursementLineSchemaType) => p.productId)
    .default([]),
})
export type DisbursementSchemaType = yup.InferType<typeof DisbursementSchema>

export const FundedProductSchema = yup.object().shape({
  productId: yup.numberEnum(getEProductList()).required().default(null),
  amount: yup
    .number()
    .required(() => t('loan.error.missingAmount'))
    .default(null),
  description: yup.string().emptyToNull().nullable().default(null),
})

export type FundedProductSchemaType = yup.InferType<typeof FundedProductSchema>

export const InterestConfigurationSchema = yup.object().shape({
  startDate: yup.dateOnly().required().default(null),
  endDate: yup.dateOnly().required().default(new DateOnly('9999-12-31')),
  rate: yup.number().min(1).max(100).required(),
})

export type InterestConfigurationSchemaType = yup.InferType<typeof InterestConfigurationSchema>

export const FinanceFeeConfigurationSchema = yup.object().shape({
  billingMethodId: yup.numberEnum(getEFeeBillingMechanismList()).required().default(null),
  amortizationStartsOn: yup.dateOnly().required().default(null),
  feeRate: yup.number().min(0).max(1).required(),
})

export type FinanceFeeConfigurationSchemaType = yup.InferType<typeof FinanceFeeConfigurationSchema>

export const PpaConfigSchema = yup.object().shape({
  bankName: yup.string().required().max(50),
  institutionNumber: yup.string().min(3).max(3).required(),
  transitNumber: yup.string().min(5).max(5).required(),
  accountNumber: yup.string().min(5).max(12).required(),
  accountHolderName: yup.string().required().max(100),
})

export type PpaConfigSchemaType = yup.InferType<typeof PpaConfigSchema>

export const AddressSchema = yup.object().shape({
  city: yup.string().required().max(50).default(null),
  civicNumber: yup.string().max(10),
  street: yup.string().required().max(100),
  apartment: yup.string().max(50).nullable().default(null),
  suite: yup.string().max(10).nullable().default(null),
  postalCode: yup.string().required(),
  stateIso: yup.mixed<EProvince>().required().oneOf(getEProvinceList()).default(null),
  country: yup.string().required().default('Canada'),
})

export type AddressSchemaType = yup.InferType<typeof AddressSchema>

export const CustomerSchema = yup.object().shape({
  firstName: yup.string().required(),
  lastName: yup.string().required(),
  middleName: yup.string(),
  address: AddressSchema.required(),
  birthDate: yup.dateOnly().required().default(null),
  languageCode: yup.numberEnum(getELanguageList()).required(),
  genderId: yup.numberEnum(getEGenderList()).required(),
  alisId: yup.number().when('monolithId', (monolithId, schema) => {
    if (monolithId) return schema
    return schema.required()
  }),
  monolithId: yup.number(),
  equifaxNumber: yup.string(),
})

export type CustomerSchemaType = yup.InferType<typeof CustomerSchema>

export const RepaymentConfigSchema = yup.object().shape({
  frequency: yup.string().required(),
  amount: yup.number().required(),
})

export type RepaymentConfigSchemaType = yup.InferType<typeof RepaymentConfigSchema>

export const LoanSchema = yup
  .object()
  .shape({
    // Identification
    financingProgramId: yup.mixed<EFinancingProgram>().required().oneOf(getEFinancingProgramList()).default(null),
    financingPlanId: yup.numberEnum(getEFinancingPlanList()).required().default(null),
    stateIso: yup.mixed<EProvince>().required().oneOf(getEProvinceList()).default(null),
    loanPurposeId: yup.mixed<ELoanPurpose>().nullable().oneOf(getELoanPurposeList()).default(null),
    contractNumber: yup.string().required().default(null),
    creditReferenceNumber: yup.number().required().default(null),
    creditApplicationId: yup.string().emptyToNull().required().default(null),
    borrowerId: yup.number().nullable().required().default(null),
    coBorrowerId: yup.number().nullable().default(null),
    coBorrowerRelationId: yup.numberEnum(getECoBorrowerRelationList()).nullable().default(null),

    // Configuration
    term: yup.numberEnum(getETermList()).required().default(null),
    paymentPlanId: yup
      .mixed<EPaymentPlan>()
      .required()
      .oneOf(getEPaymentPlanList())
      .default(EPaymentPlan.RegularDailyInterests),
    activationPlannedOn: yup.dateOnly().required().default(null),
    firstPaymentExpectedOn: yup.dateOnly().required().default(null),
    interestRoundingMethod: yup.numberEnum(getEInterestRoundingMethodList()).required().default(null),
    interestComputeMethod: yup
      .mixed<EInterestComputeMethod>()
      .required()
      .oneOf(getEInterestComputeMethodList())
      .default(null),

    interestConfigs: yup
      .array()
      .of(InterestConfigurationSchema)
      .min(1)
      .required()
      .test(
        'startsEnds-Invalid',
        () => t('loan.error.endsOnBeforeStartsOn'),
        (value) => {
          return value.every((config) => config.startDate < config.endDate)
        },
      )
      .test(
        'endsDate-Invalid',
        () => t('loan.error.configEndDateInvalid'),
        (value) => {
          return value.some((config) => config.endDate.getTime() === new DateOnly('9999-12-31').getTime())
        },
      )
      .test(
        'startsDate-SequenceInvalid',
        () => t('loan.error.configSequenceInvalid'),
        (value) => {
          return value.every((config) =>
            value.some(
              (config2) =>
                config.endDate.getTime() === config2.startDate.getTime() ||
                config.endDate.getTime() === new DateOnly('9999-12-31').getTime(),
            ),
          )
        },
      )
      .default([]),

    financeFeeConfig: FinanceFeeConfigurationSchema.required(),

    ppaConfig: PpaConfigSchema.required(),

    fundedProducts: yup
      .array()
      .of(FundedProductSchema)
      .min(1)
      .required()
      .unique(
        (p: FundedProductSchemaType) => p.productId,
        () => t('loan.error.uniqueFundedProduct'),
      )
      .default([
        { productId: EProduct.CapitalFinanced, amount: null as unknown as number, description: null },
        { productId: EProduct.FinanceFee, amount: null as unknown as number, description: null },
        { productId: EProduct.IFinanceInsurance, amount: null as unknown as number, description: null },
      ]),
    nsfFee: yup.number().required().default(30),
    adminFee: yup.number().required().default(25),
    repaymentConfig: RepaymentConfigSchema.required(),
    disbursements: yup
      .array()
      .of(DisbursementSchema)
      .min(1)
      .required()
      .unique((p: DisbursementSchemaType) => p.payeeId)
      .test(
        'productId-missing',
        () => t('loan.error.productIdMissingInFundedProducts'),
        (value, ctx) => {
          const fundedProducts = (ctx.parent as { fundedProducts: FundedProductSchemaType[] }).fundedProducts
          const productIds = new Set<EProduct>(fundedProducts.map((p: FundedProductSchemaType) => p.productId))
          return value.every((p) => p.lines.every((l) => productIds.has(l.productId)))
        },
      )
      .test(
        'max-one-borrower',
        () => t('loan.error.maxOneBorrower'),
        (value) => {
          const borrowerCount = value.filter((p) => p.payeeType === EDisbursementTarget.Borrower).length
          return borrowerCount <= 1
        },
      )
      .default([]),
  })
  .test(
    'firstPaymentExpectedOn-Invalid',
    () => t('loan.error.firstPaymentEarlierThanActivationPlannedOn'),
    (value) => {
      return value.activationPlannedOn < value.firstPaymentExpectedOn
    },
  )

export type LoanSchemaType = yup.InferType<typeof LoanSchema>

export const NewLoanDtoSchema = yup.object().shape({
  loanData: LoanSchema.required(),

  // Borrower / Coborrower
  borrower: CustomerSchema.required(),
  coBorrower: CustomerSchema.nullable().default(null),
})

export type NewLoanDtoSchemaType = yup.InferType<typeof NewLoanDtoSchema>

export const Metro2ReportingSchema = yup.object().shape({
  id: yup.string().nullable(),
  reported: yup.boolean().required(),
  metro2OverrideDate: yup
    .dateOnly()
    .required()
    .test(
      'metro2OverrideDate-Invalid',
      () => t('loan.error.earlierOfCurrentMonth'),
      (value) => {
        const today = new DateOnly()
        const firstDayOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1)
        return value >= firstDayOfCurrentMonth // Ensure the override date is not anterior to the current month
      },
    ),
  metro2ReportingReason: yup.numberEnum(getEMetro2ReportingReasonList()).required().default(null),
  createdOn: yup.date().required(),
  description: yup.string().nullable(),
  createdByUserFullname: yup.string().required(),
  createdByUserId: yup.string().required(),
})
export type Metro2ReportingSchemaType = yup.InferType<typeof Metro2ReportingSchema>
